import { useState, useRef, useEffect } from "react";
import { Link, useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Bell,
  BellOff,
  Building2,
  Calendar,
  ExternalLink,
  FileText,
  Link2,
  Paperclip,
  Plus,
  Trophy,
  Award,
  Trash2,
  FolderOpen,
  XCircle,
  Ban,
  CircleOff,
  ChevronRight,
  FlaskConical,
} from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { formatDate, formatDateTime } from "@/utils/formatDate";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { PageLoader } from "@/components/ui/Spinner";
import {
  useTender,
  useAddTenderRound,
  useUploadTenderAttachment,
  useDeleteTenderAttachment,
  useUploadTenderMaterial,
  useDeleteTenderMaterial,
  useMarkTenderWon,
  useSetTenderStatus,
  useWatchTender,
  useDeleteTender,
  useStartTenderConstruction,
} from "../hooks/useTenders";
import {
  TENDER_STATUS_COLORS,
  TENDER_STATUS_LABELS,
  type TenderStatus,
} from "../types";
import { TenderRoundSection, type ZEImportSummary } from "../components/TenderRoundSection";
import { TenderConversation } from "../components/TenderConversation";
import { TenderWonModal } from "../components/TenderWonModal";
import { TenderScopeControl } from "../components/TenderScopeControl";
import { TenderPricingClaimControl } from "../components/TenderPricingClaimControl";
import { extractTenderError } from "../utils";
import type { MarkTenderWonFormData } from "../types";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useZETenderAiAnalysis } from "@/features/ze_tenders/hooks";

export function TenderDetailPage({
  embedded = false,
  zeVariants,
  onLoadZESummary,
  onRecomputeRoadNeighbors,
  roadNeighborsBusy,
}: {
  embedded?: boolean;
  zeVariants?: {
    id: string;
    label: string;
    mpzpCoveragePct?: number | null;
    mpzpUnknownPct?: number | null;
    mpzpComputedAt?: string | null;
    hasMpzpGap?: boolean;
    hasProtectedAreas?: boolean;
  }[];
  onLoadZESummary?: (variantId: string) => Promise<ZEImportSummary>;
  onRecomputeRoadNeighbors?: (variantId: string) => Promise<void>;
  roadNeighborsBusy?: boolean;
} = {}) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);

  // Mention notifications link to /tenders/<id>?tab=conversation&message=<id>.
  const highlightMessageId = searchParams.get("message");
  const focusConversation = searchParams.get("tab") === "conversation" || !!highlightMessageId;

  const { data: tender, isLoading, isError } = useTender(id!);
  const { data: aiAnalysis } = useZETenderAiAnalysis(id);
  usePageTitle(tender?.title);
  const addRound = useAddTenderRound(id!);
  const uploadAttachment = useUploadTenderAttachment(id!);
  const deleteAttachment = useDeleteTenderAttachment(id!);
  const uploadMaterial = useUploadTenderMaterial(id!);
  const deleteMaterial = useDeleteTenderMaterial(id!);
  const markWon = useMarkTenderWon(id!);
  const setStatus = useSetTenderStatus(id!);
  const watchTenderMutation = useWatchTender(id!);
  const deleteTenderMutation = useDeleteTender();
  const startConstruction = useStartTenderConstruction(id!);

  const [wonModalOpen, setWonModalOpen] = useState(false);
  const [wonError, setWonError] = useState<string | null>(null);
  const [constructionError, setConstructionError] = useState<string | null>(null);
  const [statusModal, setStatusModal] = useState(false);
  const [addRoundModal, setAddRoundModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [roundDeadline, setRoundDeadline] = useState("");
  const [roundNotes, setRoundNotes] = useState("");

  const attachFileRef = useRef<HTMLInputElement>(null);
  const materialFileRef = useRef<HTMLInputElement>(null);
  const [isDraggingAttach, setIsDraggingAttach] = useState(false);
  const [isDraggingMaterial, setIsDraggingMaterial] = useState(false);

  const isDirector = user?.role === "director";
  const isDirectorOrCoordinator = isDirector || user?.role === "coordinator";
  // Zmiana zakresu przetargu idzie przez PATCH /tenders/<id>/, którego backend broni
  // IsTechnicalDirector (CEO albo dyrektor techniczny). Kontrolka musi pytać o to samo —
  // inaczej koordynator widzi klikalną odznakę i dostaje 403.
  const canEditScope =
    !!user?.is_ceo || (isDirector && !!user?.is_technical_director);
  const canCalculatePricing = !!user?.has_calculate_pricing_permission;
  const canApprovePricing = !!user?.has_approve_pricing_permission;
  const canIssueOffers = !!user?.has_issue_offers_permission;

  // A ZE tender shares its PK with the plain tender row; visiting /tenders/:id
  // for one should land on the dedicated ZE workspace. Skip when embedded — the
  // ZE workspace itself renders this page, so redirecting would loop. The query
  // string rides along, so a mention link still opens the conversation there.
  const shouldRedirectToZE = !embedded && !!tender?.is_ze_tender;
  useEffect(() => {
    if (shouldRedirectToZE && id) {
      navigate(`/ze-tenders/${id}${location.search}`, { replace: true });
    }
  }, [shouldRedirectToZE, id, navigate, location.search]);

  if (isLoading || shouldRedirectToZE) return <PageLoader />;
  if (isError || !tender) {
    return (
      <div className="text-center py-16 text-gray-400">
        <p>Nie znaleziono przetargu.</p>
        <Link
          to="/tenders"
          className="text-[var(--color-primary)] hover:underline text-sm mt-2 block"
        >
          ← Wróć do listy
        </Link>
      </div>
    );
  }

  const handleMarkWon = async (data: MarkTenderWonFormData) => {
    setWonError(null);
    try {
      await markWon.mutateAsync(data);
      setWonModalOpen(false);
    } catch (err) {
      // Bez tego odrzucone żądanie ginęło cicho — modal zostawał otwarty bez śladu błędu.
      setWonError(extractTenderError(err));
    }
  };

  const handleAddRound = async (e: React.FormEvent) => {
    e.preventDefault();
    await addRound.mutateAsync({
      offer_deadline: roundDeadline || undefined,
      notes: roundNotes,
    });
    setAddRoundModal(false);
    setRoundDeadline("");
    setRoundNotes("");
  };

  const handleUploadAttachment = async (file: File) => {
    await uploadAttachment.mutateAsync({ file, name: file.name });
  };

  const handleUploadMaterial = async (file: File) => {
    await uploadMaterial.mutateAsync({ file, name: file.name });
  };

  const handleDropAttach = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingAttach(false);
    const files = Array.from(e.dataTransfer.files);
    for (const f of files)
      await uploadAttachment.mutateAsync({ file: f, name: f.name });
  };

  const handleDropMaterial = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingMaterial(false);
    const files = Array.from(e.dataTransfer.files);
    for (const f of files)
      await uploadMaterial.mutateAsync({ file: f, name: f.name });
  };

  return (
    <div>
      {/* Back + header — hidden when embedded (parent workspace shows its own title) */}
      <div className="mb-6">
        {!embedded && (
          <>
            <Link
              to="/tenders"
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-3"
            >
              <ArrowLeft size={14} /> Przetargi
            </Link>

            <div className="flex items-start gap-4 flex-wrap mb-4">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {tender.title}
                    </h1>
                    <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1" />
                  </div>
                  <Badge
                    color={TENDER_STATUS_COLORS[tender.status as TenderStatus]}
                  >
                    {TENDER_STATUS_LABELS[tender.status as TenderStatus]}
                  </Badge>
                  <TenderScopeControl
                    tenderId={tender.id}
                    workScope={tender.work_scope}
                    workScopeSource={tender.work_scope_source}
                    canEdit={canEditScope}
                  />
                </div>
                {tender.reference_number && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 font-mono">
                    {tender.reference_number}
                  </p>
                )}
              </div>
            </div>
          </>
        )}

        <div className="flex items-center justify-between gap-4 flex-wrap">
          {embedded && (
            <div className="flex items-center gap-2">
              <Badge
                color={TENDER_STATUS_COLORS[tender.status as TenderStatus]}
              >
                {TENDER_STATUS_LABELS[tender.status as TenderStatus]}
              </Badge>
              <TenderScopeControl
                tenderId={tender.id}
                workScope={tender.work_scope}
                workScopeSource={tender.work_scope_source}
                canEdit={canEditScope}
              />
              {tender.reference_number && (
                <span className="text-sm text-gray-500 font-mono">
                  {tender.reference_number}
                </span>
              )}
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap ml-auto">
            {/* „Do zrobienia" + Przejmij/Zwolnij wycenę — kto liczy tę turę */}
            <TenderPricingClaimControl tender={tender} />

            {/* Watch button — always visible for internal users */}
            <button
              onClick={() => watchTenderMutation.mutate(!tender.is_watching)}
              disabled={watchTenderMutation.isPending}
              title={
                tender.is_watching
                  ? "Wypisz się z powiadomień"
                  : "Subskrybuj powiadomienia"
              }
              className={`p-1.5 rounded-lg transition-colors ${
                tender.is_watching
                  ? "text-[var(--color-primary)] bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100"
                  : "text-gray-400 dark:text-gray-500 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
              }`}
            >
              {tender.is_watching ? (
                <Bell size={18} className="fill-blue-100" />
              ) : (
                <BellOff size={18} />
              )}
            </button>

            {/* Symulacja wyceny — wejście do kosztorysu/marży przed decyzją (CEO) */}
            {user?.is_ceo && (
              <Link
                to={`/finances/order-simulations?source=tender&ref=${tender.id}&name=${encodeURIComponent("Wycena: " + tender.title)}&label=${encodeURIComponent(tender.title)}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-[var(--color-primary)] hover:bg-blue-50 dark:hover:bg-blue-900/30"
                title="Symuluj koszt i marżę tego przetargu"
              >
                <FlaskConical size={16} /> Symuluj wycenę
              </Link>
            )}

            {/* Status actions */}
            {isDirectorOrCoordinator && tender.status === "open" && (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setAddRoundModal(true)}
                >
                  <Plus size={13} /> Dodaj turę
                </Button>
                {isDirector && (
                  <Button
                    size="sm"
                    onClick={() => setWonModalOpen(true)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Trophy size={13} /> Oznacz jako wygrany
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setStatusModal(true)}
                >
                  Zmień status
                </Button>
              </>
            )}
            {/* Soft win ("Wygrany - nieoficjalnie"): confirm officially (creates the
                Project via mark-won), change to a negative outcome, or revert to open. */}
            {isDirectorOrCoordinator && tender.status === "won_unofficial" && (
              <>
                {isDirector && (
                  <Button
                    size="sm"
                    onClick={() => setWonModalOpen(true)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Trophy size={13} /> Potwierdź oficjalnie
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setStatusModal(true)}
                >
                  Zmień status
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  loading={setStatus.isPending}
                  onClick={() => setStatus.mutate("open")}
                >
                  Cofnij do otwartego
                </Button>
              </>
            )}
            {isDirector &&
              (tender.status === "lost" ||
                tender.status === "cancelled" ||
                tender.status === "not_starting") && (
                <Button
                  size="sm"
                  variant="secondary"
                  loading={setStatus.isPending}
                  onClick={() => setStatus.mutate("open")}
                >
                  Przywróć do otwarcia
                </Button>
              )}
            {isDirector && (
              <Button
                size="sm"
                onClick={() => setDeleteModal(true)}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 size={13} /> Usuń przetarg
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Info card */}
          <div className="bg-white dark:bg-gray-800 p-5">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
              Informacje
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div className="flex items-start gap-2">
                <Building2
                  size={15}
                  className="text-gray-400 mt-0.5 flex-shrink-0"
                />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Klient
                  </p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {tender.client_name}
                  </p>
                  {tender.client_branch_name && (
                    <p className="text-xs text-[var(--color-primary)] dark:text-blue-400 mt-0.5">
                      Oddział: {tender.client_branch_name}
                    </p>
                  )}
                </div>
              </div>
              {tender.source_url && (
                <div className="flex items-start gap-2 col-span-2">
                  <Link2
                    size={15}
                    className="text-gray-400 mt-0.5 flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      URL ogłoszenia
                    </p>
                    <a
                      href={tender.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-[var(--color-primary)] hover:underline truncate max-w-full"
                    >
                      <ExternalLink size={12} />
                      {tender.source_url}
                    </a>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-2">
                <Calendar
                  size={15}
                  className="text-gray-400 mt-0.5 flex-shrink-0"
                />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Termin oferty
                  </p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {formatDateTime(tender.offer_deadline)}
                  </p>
                </div>
              </div>
              {tender.coordinator_name && (
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Koordynator
                  </p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {tender.coordinator_name}
                  </p>
                </div>
              )}
              {tender.linked_project && (
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Przedsięwzięcie
                  </p>
                  <Link
                    to={`/projects/${tender.linked_project}`}
                    className="font-medium text-[var(--color-primary)] hover:underline"
                  >
                    Otwórz projekt →
                  </Link>
                  {/* Budowa to OSOBNE przedsięwzięcie, zakładane osobnym kliknięciem:
                      między wygraniem P+W a wejściem na budowę mijają miesiące, przez
                      które puste przedsięwzięcie zaśmiecałoby każdą listę. */}
                  {tender.work_scope === "projekt_i_wykonawstwo" &&
                    isDirector && (
                      <div className="mt-1">
                        <button
                          disabled={startConstruction.isPending}
                          onClick={() =>
                            startConstruction.mutate(undefined, {
                              onSuccess: (r) => navigate(`/projects/${r.project_id}`),
                              onError: (e) =>
                                setConstructionError(extractTenderError(e)),
                            })
                          }
                          className="text-xs text-[var(--color-primary)] hover:underline disabled:opacity-50"
                          title="Załóż osobne przedsięwzięcie budowlane w tej samej grupie"
                        >
                          + Załóż budowę
                        </button>
                        {constructionError && (
                          <p className="mt-0.5 font-mono text-[11px] text-red-600">
                            {constructionError}
                          </p>
                        )}
                      </div>
                    )}
                </div>
              )}
              {tender.latest_offer_value && (
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Ostatnia oferta
                  </p>
                  <p className="font-semibold text-gray-900 dark:text-gray-100 font-mono">
                    {Number(tender.latest_offer_value).toLocaleString("pl-PL", { useGrouping: "always" })}{" "}
                    PLN
                  </p>
                </div>
              )}
            </div>
            {tender.description && (
              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Opis
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {tender.description}
                </p>
              </div>
            )}
          </div>

          {/* Rounds */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Tury przetargu ({tender.rounds.length})
              </h2>
            </div>
            {tender.rounds.length === 0 ? (
              <div className="text-center py-8 text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
                <p className="text-sm">
                  Brak tur. Pierwsza tura tworzona jest automatycznie.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Najnowsza tura (najwyższy round_number) na górze. previousRound i auto-otwarcie
                    liczymy z porządku rosnącego (chronologicznego), więc „poprzednia tura" oraz
                    domyślnie otwarta najnowsza działają niezależnie od kierunku wyświetlania. */}
                {[...tender.rounds]
                  .sort((a, b) => a.round_number - b.round_number)
                  .map((round, idx, asc) => (
                    <TenderRoundSection
                      key={round.id}
                      tenderId={tender.id}
                      round={round}
                      previousRound={idx > 0 ? asc[idx - 1] : undefined}
                      defaultOpen={idx === asc.length - 1}
                      zeVariants={zeVariants}
                      onLoadZESummary={onLoadZESummary}
                      onRecomputeRoadNeighbors={onRecomputeRoadNeighbors}
                      roadNeighborsBusy={roadNeighborsBusy}
                    />
                  ))
                  .reverse()}
              </div>
            )}
          </div>

          {/* Conversation */}
          <div className="bg-white dark:bg-gray-800 p-5">
            <TenderConversation
              tenderId={tender.id}
              messages={tender.messages}
              highlightMessageId={highlightMessageId}
              focusConversation={focusConversation}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          {/* Contract file (won) */}
          {tender.status === "won" && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Trophy size={16} className="text-green-600" />
                <h3 className="text-sm font-semibold text-green-700 dark:text-green-400">
                  Przetarg wygrany
                </h3>
              </div>
              {tender.won_at && (
                <p className="text-xs text-green-600 dark:text-green-500 mb-3">
                  {formatDate(tender.won_at)}
                </p>
              )}
              {/* Cena wprost na karcie — dyrektor nie musi otwierać PDF-a umowy,
                  żeby poznać wartość (ten sam krąg odbiorców, co link poniżej). */}
              {tender.contract_value && (
                <div className="mb-3">
                  <p className="text-xs text-green-600 dark:text-green-500">
                    Wartość umowy (netto)
                  </p>
                  <p className="font-semibold text-green-800 dark:text-green-300 font-mono">
                    {Number(tender.contract_value).toLocaleString("pl-PL", { useGrouping: "always" })}{" "}
                    PLN
                  </p>
                </div>
              )}
              {tender.contract_file_url ? (
                <a
                  href={tender.contract_file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-[var(--color-primary)] hover:underline"
                >
                  <FileText size={13} /> Pobierz umowę
                </a>
              ) : (
                <p className="text-xs text-green-600 dark:text-green-500">
                  Brak załączonej umowy.
                </p>
              )}
            </div>
          )}

          {/* Soft win ("Wygrany - nieoficjalnie") — no Project yet, confirm officially */}
          {tender.status === "won_unofficial" && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Award size={16} className="text-amber-600" />
                <h3 className="text-sm font-semibold text-amber-700 dark:text-amber-400">
                  Wygrany - nieoficjalnie
                </h3>
              </div>
              <p className="text-xs text-amber-700 dark:text-amber-500">
                Wstępna wygrana bez formalnego potwierdzenia — nie utworzono
                jeszcze przedsięwzięcia.
                {isDirector
                  ? " Po oficjalnym rozstrzygnięciu użyj „Potwierdź oficjalnie”."
                  : ""}
              </p>
              {(() => {
                const lastRound = [...(tender.rounds ?? [])].sort(
                  (a, b) => b.round_number - a.round_number,
                )[0];
                const tenderDate = lastRound?.effective_deadline || tender.offer_deadline;
                return (
                  <dl className="mt-3 space-y-1 text-xs text-amber-700 dark:text-amber-500">
                    <div className="flex justify-between gap-3">
                      <dt className="opacity-80">Data nieoficjalnej informacji</dt>
                      <dd className="font-medium">{formatDate(tender.won_unofficial_at)}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="opacity-80">Termin oferty / ostatniej tury</dt>
                      <dd className="font-medium">{formatDate(tenderDate)}</dd>
                    </div>
                  </dl>
                );
              })()}
            </div>
          )}

          {/* Attachments */}
          <div
            className={`rounded-xl border-2 p-4 transition-colors ${
              isDraggingAttach
                ? "border-[var(--color-primary)] bg-blue-50 dark:bg-blue-900/20"
                : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              if (isDirectorOrCoordinator) setIsDraggingAttach(true);
            }}
            onDragLeave={() => setIsDraggingAttach(false)}
            onDrop={isDirectorOrCoordinator ? handleDropAttach : undefined}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Paperclip size={15} className="text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Załączniki ({tender.attachments.length})
                </h3>
              </div>
              {isDirectorOrCoordinator && (
                <button
                  onClick={() => attachFileRef.current?.click()}
                  className="text-[var(--color-primary)] hover:text-[var(--color-primary)] text-xs"
                >
                  + Dodaj
                </button>
              )}
            </div>
            <input
              ref={attachFileRef}
              type="file"
              multiple
              className="hidden"
              onChange={async (e) => {
                const files = Array.from(e.target.files ?? []);
                for (const f of files) await handleUploadAttachment(f);
                e.target.value = "";
              }}
            />
            {isDraggingAttach ? (
              <p className="text-xs text-[var(--color-primary)] text-center py-2">
                Upuść pliki tutaj...
              </p>
            ) : tender.attachments.length === 0 ? (
              <p className="text-xs text-gray-400 dark:text-gray-500">
                Brak załączników.
                {isDirectorOrCoordinator &&
                  " Przeciągnij plik lub kliknij + Dodaj."}
              </p>
            ) : (
              <ul className="space-y-1.5">
                {tender.attachments.map((att) => (
                  <li
                    key={att.id}
                    className="flex items-center justify-between gap-2"
                  >
                    <a
                      href={att.file_url ?? "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={att.name}
                      className="text-xs text-[var(--color-primary)] hover:underline truncate flex items-center gap-1"
                    >
                      <FileText size={11} /> {att.name}
                    </a>
                    {isDirectorOrCoordinator && (
                      <button
                        onClick={() => deleteAttachment.mutate(att.id)}
                        className="text-gray-400 hover:text-red-500 flex-shrink-0"
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Material files */}
          <div
            className={`rounded-xl border-2 p-4 transition-colors ${
              isDraggingMaterial
                ? "border-[var(--color-primary)] bg-blue-50 dark:bg-blue-900/20"
                : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              if (isDirectorOrCoordinator) setIsDraggingMaterial(true);
            }}
            onDragLeave={() => setIsDraggingMaterial(false)}
            onDrop={isDirectorOrCoordinator ? handleDropMaterial : undefined}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <FolderOpen size={15} className="text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Materiały ({tender.material_files.length})
                </h3>
              </div>
              {isDirectorOrCoordinator && (
                <button
                  onClick={() => materialFileRef.current?.click()}
                  className="text-[var(--color-primary)] hover:text-[var(--color-primary)] text-xs"
                >
                  + Dodaj
                </button>
              )}
            </div>
            <input
              ref={materialFileRef}
              type="file"
              multiple
              className="hidden"
              onChange={async (e) => {
                const files = Array.from(e.target.files ?? []);
                for (const f of files) await handleUploadMaterial(f);
                e.target.value = "";
              }}
            />
            {isDraggingMaterial ? (
              <p className="text-xs text-[var(--color-primary)] text-center py-2">
                Upuść pliki tutaj...
              </p>
            ) : tender.material_files.length === 0 ? (
              <p className="text-xs text-gray-400 dark:text-gray-500">
                Brak plików materiałów.
                {isDirectorOrCoordinator &&
                  " Przeciągnij plik lub kliknij + Dodaj."}
              </p>
            ) : (
              <ul className="space-y-1.5">
                {tender.material_files.map((mat) => (
                  <li
                    key={mat.id}
                    className="flex items-center justify-between gap-2"
                  >
                    <a
                      href={mat.file_url ?? "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={mat.name}
                      className="text-xs text-[var(--color-primary)] hover:underline truncate flex items-center gap-1"
                    >
                      <FileText size={11} /> {mat.name}
                    </a>
                    {isDirectorOrCoordinator && (
                      <button
                        onClick={() => deleteMaterial.mutate(mat.id)}
                        className="text-gray-400 hover:text-red-500 flex-shrink-0"
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Mark Won Modal */}
      <TenderWonModal
        open={wonModalOpen}
        onClose={() => { setWonModalOpen(false); setWonError(null); }}
        tenderTitle={tender.title}
        referenceNumber={tender.reference_number}
        defaultCoordinatorId={tender.coordinator}
        latestOfferValue={tender.latest_offer_value}
        offerDeadline={tender.offer_deadline}
        defaultOddzial={aiAnalysis?.oddzial}
        defaultRejon={aiAnalysis?.signals?.energa_rejon}
        clientId={tender.client}
        onConfirm={handleMarkWon}
        isPending={markWon.isPending}
        errorMessage={wonError}
      />

      {/* Add Round Modal */}
      <Modal
        open={addRoundModal}
        onClose={() => setAddRoundModal(false)}
        title="Dodaj kolejną turę"
        size="sm"
      >
        <form onSubmit={handleAddRound} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Termin oferty (opcjonalnie, nadpisuje termin przetargu)
            </label>
            <input
              type="datetime-local"
              value={roundDeadline}
              onChange={(e) => setRoundDeadline(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Uwagi do tury
            </label>
            <textarea
              value={roundNotes}
              onChange={(e) => setRoundNotes(e.target.value)}
              rows={2}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] resize-none"
              placeholder="Informacje o kolejnej turze..."
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setAddRoundModal(false)}
            >
              Anuluj
            </Button>
            <Button type="submit" size="sm" loading={addRound.isPending}>
              Dodaj turę
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Tender Modal */}
      <Modal
        open={deleteModal}
        onClose={() => setDeleteModal(false)}
        title="Usuń przetarg"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Czy na pewno chcesz usunąć przetarg{" "}
            <span className="font-semibold">„{tender.title}"</span>? Ta operacja
            jest nieodwracalna — zostaną usunięte wszystkie tury, wyceny i
            oferty.
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setDeleteModal(false)}
            >
              Anuluj
            </Button>
            <Button
              size="sm"
              loading={deleteTenderMutation.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() =>
                deleteTenderMutation.mutate(tender.id, {
                  onSuccess: () => navigate("/tenders"),
                })
              }
            >
              Usuń
            </Button>
          </div>
        </div>
      </Modal>

      {/* Change Status Modal */}
      <Modal
        open={statusModal}
        onClose={() => setStatusModal(false)}
        title="Zmień status przetargu"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Wybierz nowy status dla przetargu:
          </p>
          <div className="space-y-2.5">
            {[
              {
                value: "won_unofficial" as const,
                icon: Award,
                desc: "Np. przy uzyskaniu e-mail od ENERGA OPERATOR z informacją wstępną",
                accent:
                  "border-amber-200 hover:border-amber-300 hover:bg-amber-50 dark:border-amber-900/40 dark:hover:border-amber-800 dark:hover:bg-amber-950/30",
                iconClass:
                  "text-amber-600 bg-amber-100 dark:text-amber-400 dark:bg-amber-950/50",
              },
              {
                value: "lost" as const,
                icon: XCircle,
                desc: "Przetarg został rozstrzygnięty na niekorzyść.",
                accent:
                  "border-red-200 hover:border-red-300 hover:bg-red-50 dark:border-red-900/40 dark:hover:border-red-800 dark:hover:bg-red-950/30",
                iconClass:
                  "text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-950/50",
              },
              {
                value: "cancelled" as const,
                icon: Ban,
                desc: "Postępowanie zostało unieważnione lub odwołane.",
                accent:
                  "border-gray-200 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:hover:border-gray-600 dark:hover:bg-gray-800/50",
                iconClass:
                  "text-gray-600 bg-gray-100 dark:text-gray-300 dark:bg-gray-800",
              },
              {
                value: "not_starting" as const,
                icon: CircleOff,
                desc: "Rezygnujemy z udziału — przetarg znika z aktywnej listy (można przywrócić).",
                accent:
                  "border-slate-200 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:hover:border-slate-600 dark:hover:bg-slate-800/50",
                iconClass:
                  "text-slate-600 bg-slate-100 dark:text-slate-300 dark:bg-slate-800",
              },
            ].map(({ value, icon: Icon, desc, accent, iconClass }) => (
              <button
                key={value}
                disabled={setStatus.isPending}
                onClick={async () => {
                  await setStatus.mutateAsync(value);
                  setStatusModal(false);
                }}
                className={`group flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${accent}`}
              >
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${iconClass}`}
                >
                  <Icon className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {TENDER_STATUS_LABELS[value]}
                  </span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    {desc}
                  </span>
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5" />
              </button>
            ))}
          </div>
          <Button
            size="sm"
            variant="secondary"
            className="w-full"
            onClick={() => setStatusModal(false)}
          >
            Anuluj
          </Button>
        </div>
      </Modal>
    </div>
  );
}
